import { FaTwitch, FaSpotify } from 'react-icons/fa';
import TwitchAppService from '../services/TwitchAppService';
import SpotifyAppService from '../services/SpotifyAppService';

/**
 * @typedef {'twitch'|'spotify'} DeveloperAppProvider
 */

/**
 * Configuration UI et services pour le wizard de création d'app développeur.
 * @type {Record<DeveloperAppProvider, object>}
 */
export const DEVELOPER_APP_SETUP = {
  twitch: {
    provider: 'twitch',
    themeModifierClass: 'developer-app-setup-modal--twitch',
    linkClassName: 'twitch-link',
    Icon: FaTwitch,
    iconClassName: 'twitch-icon',
    modalTitle: "Configuration de l'application Twitch",
    modalSubtitle:
      "Permettez à n'importe qui d'utiliser le bot sur sa chaîne",
    editModalTitle: "Modifier l'application Twitch",
    editModalSubtitle: "Modifiez les paramètres de votre application Twitch",
    step1Title: 'Créer une application Twitch',
    step1Intro:
      "Pour utiliser le bot sur votre chaîne, vous devez d'abord créer une application Twitch :",
    consoleUrl: 'https://dev.twitch.tv/console/apps',
    consoleActionLabel: 'Create Application',
    nameExamplePlaceholder: 'Mon Bot Twitch',
    step2TitleCreate: "Configurer les informations de l'application",
    step2TitleEdit: "Modifier l'application",
    step2IntroCreate:
      'Récupérez les informations de votre application Twitch :',
    step2IntroEdit: 'Modifiez les informations de votre application Twitch :',
    clientIdHelp: 'Trouvé dans la section "Client ID" de votre application Twitch',
    clientSecretHelp:
      'Cliquez sur "New Secret" pour générer un nouveau secret',
    redirectUriFieldLabel: 'OAuth Redirect URL',
    redirectUriHelp:
      "Doit correspondre exactement à l'URL configurée dans Twitch",
    progressLabel1: "Créer l'app",
    progressLabel2: 'Configurer',
    getSuggestedRedirectUri: () => TwitchAppService.getSuggestedRedirectUri(),
    getFallbackSuggestedUri: () =>
      `${typeof window !== 'undefined' ? window.location.origin : ''}/auth/twitch/callback`,
    createApp: (data) => TwitchAppService.createApp(data),
    updateApp: (data) => TwitchAppService.updateApp(data),
  },
  spotify: {
    provider: 'spotify',
    themeModifierClass: 'developer-app-setup-modal--spotify',
    linkClassName: 'spotify-link',
    Icon: FaSpotify,
    iconClassName: 'spotify-icon',
    modalTitle: "Configuration de l'application Spotify",
    modalSubtitle:
      "Permettez à n'importe qui de contrôler la musique sur votre stream",
    editModalTitle: "Modifier l'application Spotify",
    editModalSubtitle: "Modifiez les paramètres de votre application Spotify",
    step1Title: 'Créer une application Spotify',
    step1Intro:
      'Pour utiliser Spotify avec le bot, vous devez d\'abord créer une application Spotify :',
    consoleUrl: 'https://developer.spotify.com/dashboard/applications',
    consoleActionLabel: 'Create an App',
    nameExamplePlaceholder: 'Mon Bot Spotify',
    step2TitleCreate: "Configurer les informations de l'application",
    step2TitleEdit: "Modifier l'application",
    step2IntroCreate:
      'Récupérez les informations de votre application Spotify :',
    step2IntroEdit: 'Modifiez les informations de votre application Spotify :',
    clientIdHelp: 'Trouvé dans la section "Client ID" de votre application Spotify',
    clientSecretHelp:
      'Cliquez sur "Show client secret" pour voir le secret',
    redirectUriFieldLabel: 'Redirect URI',
    redirectUriHelp:
      "Doit correspondre exactement à l'URI configurée dans Spotify",
    progressLabel1: "Créer l'app",
    progressLabel2: 'Configurer',
    getSuggestedRedirectUri: () => SpotifyAppService.getSuggestedRedirectUri(),
    getFallbackSuggestedUri: () => {
      if (typeof window === 'undefined') return '';
      const origin = window.location.origin.replace('localhost', '127.0.0.1');
      return `${origin}/auth/user-spotify/callback`;
    },
    createApp: (data) => SpotifyAppService.createApp(data),
    updateApp: (data) => SpotifyAppService.updateApp(data),
  },
};

/**
 * @param {DeveloperAppProvider} provider
 */
export function getDeveloperAppSetupConfig(provider) {
  const config = DEVELOPER_APP_SETUP[provider];
  if (!config) {
    throw new Error(`Fournisseur d'app inconnu: ${provider}`);
  }
  return config;
}
